import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader } from "../../ui/card";
import type { ProPersonCardRender } from "../../../lib/pro-agent-types";
import { personRoute } from "../../../lib/routes";
import { verificationTierLabel } from "../../tier/tier";

// get_person read widget (Person Model Completion M3 — collapses the retired
// pair of separate single-record cards into ONE card, since there is one
// kind of record: a person). It confirms nothing and has no action buttons; it
// exists so that when the pro asks about one person the copilot renders
// trustworthy, DB-sourced facts in a card instead of paraphrasing them in
// prose (a false-confirmation guard — the card is the tool's real output).
//
// sill_status/enrichment_state are always present and drive the status chip
// and the enrichment note; the connected-only fields (verification_tier,
// readiness_label, affordability band) are only ever populated when
// enrichment_state === "available" — this card never derives or recomputes
// a value, so an agent's card cannot show a magnitude the agent lens omits.

const SILL_STATUS_LABEL: Record<string, string> = {
  not_invited: "Not invited",
  invited: "Invited",
  joined_sill: "Joined Sill",
  connected: "Connected",
};

const ENRICHMENT_NOTE: Record<string, string> = {
  not_connected: "No Sill data yet",
  locked: "Upgrade to see readiness",
  unavailable: "Readiness read failed — retry",
};

function FactRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <dt className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
        {label}
      </dt>
      <dd className="min-w-0 truncate text-right text-sm text-fg">{value}</dd>
    </div>
  );
}

// formatK renders a whole-dollar figure as a compact "$650K" / "$1.2M"
// string. Small and local — the shared merge-preview formatter isn't
// exported and this card only needs the compact form.
function formatK(n: number | undefined): string | null {
  if (n == null) return null;
  if (n < 1000) return `$${n}`;
  if (n < 1_000_000) return `$${Math.round(n / 1000)}K`;
  const m = Math.round((n / 1_000_000) * 10) / 10;
  return `$${m}M`;
}

// bandDisplay returns null (row hidden) when nothing is shared — never a
// fabricated $0.
function bandDisplay(card: ProPersonCardRender): string | null {
  const lo = formatK(card.affordability_band_low);
  const hi = formatK(card.affordability_band_high);
  if (lo && hi) return `${lo}–${hi}`;
  if (hi) return `Up to ${hi}`;
  return null;
}

export function PersonCard({ card }: { card: ProPersonCardRender }) {
  const statusLabel = SILL_STATUS_LABEL[card.sill_status] ?? card.sill_status;
  const enrichmentNote =
    card.enrichment_state === "available"
      ? null
      : (ENRICHMENT_NOTE[card.enrichment_state] ?? null);
  const band = bandDisplay(card);
  const verificationTier = verificationTierLabel(card.verification_tier);

  return (
    <Card data-testid="person-card">
      <CardHeader className="gap-1">
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 truncate text-sm font-semibold text-fg">
            {card.name || "Person"}
          </p>
          <span
            data-testid="person-card-status"
            className="shrink-0 text-xs font-semibold text-fg-muted"
          >
            {statusLabel}
          </span>
        </div>
        {card.readiness_label ? (
          <p className="text-xs text-fg-muted">{card.readiness_label}</p>
        ) : null}
        {enrichmentNote ? (
          <p
            data-testid="person-card-enrichment-note"
            className="text-xs text-fg-muted"
          >
            {enrichmentNote}
          </p>
        ) : null}
      </CardHeader>

      <CardContent className="pt-0">
        <dl className="divide-y divide-border-soft border-t border-border-soft">
          {band ? <FactRow label="Affordability" value={band} /> : null}
          {/* The wire carries the raw enum (pro_book.go assigns
              model.FinancialVerificationTier verbatim), so map it to the
              display label the rest of the Pro app uses — rendering it
              straight showed the pro "Verification: connected". */}
          {verificationTier ? (
            <FactRow label="Verification" value={verificationTier} />
          ) : null}
          {card.area ? <FactRow label="Area" value={card.area} /> : null}
          {card.horizon ? (
            <FactRow label="Timeline" value={card.horizon} />
          ) : null}
        </dl>

        {card.email || card.phone ? (
          <div
            data-testid="person-card-coords"
            className="flex flex-col gap-1.5 border-t border-border-soft py-2.5"
          >
            {card.email ? (
              <a
                href={`mailto:${card.email}`}
                className="inline-flex items-center gap-1.5 text-sm text-pro-ink hover:underline"
              >
                <Mail size={13} aria-hidden />
                <span className="min-w-0 truncate">{card.email}</span>
              </a>
            ) : null}
            {card.phone ? (
              <a
                href={`tel:${card.phone}`}
                className="inline-flex items-center gap-1.5 text-sm text-fg"
              >
                <Phone size={13} aria-hidden />
                <span className="min-w-0 truncate">{card.phone}</span>
              </a>
            ) : null}
          </div>
        ) : null}

        {card.tags && card.tags.length > 0 ? (
          <div
            data-testid="person-card-tags"
            className="mt-2.5 flex flex-wrap gap-1"
          >
            {card.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center rounded-md bg-pro-soft px-1.5 py-px text-[11px] font-semibold text-pro-ink"
              >
                {tag}
              </span>
            ))}
          </div>
        ) : null}

        {card.note ? (
          <p className="mt-2.5 border-l-2 border-border pl-2.5 text-xs italic text-fg-muted">
            {card.note}
          </p>
        ) : null}
      </CardContent>

      <CardFooter className="mt-1 rounded-b-lg border-t border-border-soft bg-muted/40 p-4">
        <Link
          to={personRoute(card.contact_id, "/people")}
          className="inline-flex items-center gap-1 text-xs font-semibold text-pro-ink hover:underline"
        >
          View person
          <ArrowRight size={13} aria-hidden />
        </Link>
      </CardFooter>
    </Card>
  );
}
